/**
 * Выделение строк ленты: multi-select (Ctrl) и фокус превью.
 */
import type { FileItem } from '@/common/types/fileItem'
import { attachmentItemsReferSameRow } from '@/common/utils/fileAttachmentsUniData'
import { ref, type Ref } from 'vue'

export type UseAttachmentSelectionOptions = {
  tapeItems: Ref<FileItem[]>
  tapeWithFilesOnly: Ref<boolean>
  showLargePreview: Ref<boolean>
}

export function useAttachmentSelection(options: UseAttachmentSelectionOptions) {
  const selectedFiles = ref<FileItem[]>([])
  const fileToPreview = ref<FileItem | null>(null)
  const previewOnlyIndex = ref(0)

  function rebindFileToPreview(resolveRow: (needle: FileItem) => FileItem | null): void {
    const current = fileToPreview.value
    if (!current) return
    const match = resolveRow(current)
    if (!match || match === current) return
    fileToPreview.value = match
  }

  function openPreviewForFile(file: FileItem | undefined): void {
    if (!file || options.tapeWithFilesOnly.value || !options.showLargePreview.value) return
    selectedFiles.value = [file]
    fileToPreview.value = file
  }

  function selectFile(file: FileItem): void {
    const index = selectedFiles.value.findIndex((f) => attachmentItemsReferSameRow(f, file))
    if (index !== -1) {
      selectedFiles.value.splice(index, 1)
    } else {
      selectedFiles.value.push(file)
    }
  }

  function setPreviewFile(event: MouseEvent | undefined, file: FileItem): void {
    if (event && (event.ctrlKey || event.metaKey)) {
      selectFile(file)
      return
    }
    selectedFiles.value = [file]
    if (options.tapeWithFilesOnly.value) return
    fileToPreview.value = file
  }

  function selectAll(): void {
    selectedFiles.value = [...options.tapeItems.value]
  }

  function clearSelection(): void {
    selectedFiles.value = []
  }

  function shiftPreviewOnly(delta: number): void {
    const next = previewOnlyIndex.value + delta
    if (next < 0 || next >= options.tapeItems.value.length) return
    previewOnlyIndex.value = next
    fileToPreview.value = options.tapeItems.value[next] ?? null
  }

  function applyAutoShowFirstElement(enabled: boolean): void {
    if (!enabled || options.tapeItems.value.length === 0) return
    const first = options.tapeItems.value[0]
    fileToPreview.value = first
    previewOnlyIndex.value = 0
    if (!options.tapeWithFilesOnly.value) {
      selectedFiles.value = [first]
    }
  }

  function isFilePreviewFocused(file: FileItem): boolean {
    if (!fileToPreview.value) return false
    return attachmentItemsReferSameRow(file, fileToPreview.value)
  }

  function getDeleteTargetFile(resolveRow: (needle: FileItem) => FileItem | null): FileItem | null {
    if (selectedFiles.value.length === 0) return null
    if (!options.tapeWithFilesOnly.value && fileToPreview.value) {
      const fromPreview = resolveRow(fileToPreview.value)
      if (fromPreview) return fromPreview
    }
    const needle = selectedFiles.value[selectedFiles.value.length - 1]!
    return resolveRow(needle)
  }

  return {
    selectedFiles,
    fileToPreview,
    previewOnlyIndex,
    rebindFileToPreview,
    openPreviewForFile,
    selectFile,
    setPreviewFile,
    selectAll,
    clearSelection,
    shiftPreviewOnly,
    applyAutoShowFirstElement,
    isFilePreviewFocused,
    getDeleteTargetFile,
  }
}
